// routes.tsx
import { RouteObject } from "react-router-dom";
import { lazy, Suspense } from "react";

// Lazy-loaded pages
const LandingPage = lazy(() => import("@/pages/landing/LandingPage"));
const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/auth/RegisterPage"));
const OAuthCallbackPage = lazy(() => import("@/pages/auth/OAuthCallbackPage"));
const CareerPathsPage = lazy(() => import("@/pages/careers/CareerPathsPage"));
const SettingsPage = lazy(() => import("@/pages/settings/SettingsPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const ContactPage = lazy(() => import("@/pages/contact/ContactPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));

// Links pages
const RecentLinksPage = lazy(() => import("@/pages/dashboard/LinksPage"));
const ModernLinksPage = lazy(() => import("@/pages/links/ModernLinksPage"));
const CollectionsPage = lazy(() => import("@/pages/dashboard/CollectionsPage"));
const TagsPage = lazy(() => import("@/pages/dashboard/TagsPage"));
const FiltersPage = lazy(() => import("@/pages/dashboard/FiltersPage"));
const ImportPage = lazy(() => import("@/pages/dashboard/ImportPage"));
const CollectionLinksPage = lazy(() => import("@/pages/links/CollectionLinksPage"));
const TagLinksPage = lazy(() => import("@/pages/links/TagLinksPage"));

// Discover pages
const MyActivityPage = lazy(() => import("@/pages/discover/MyActivityPage"));
const NetworkActivityPage = lazy(() => import("@/pages/discover/NetworkActivityPage"));

// Profile pages
const AccountInfoPage = lazy(() => import("@/pages/profile/AccountInfoPage"));
const SubscriptionPlanPage = lazy(() => import("@/pages/profile/SubscriptionPlanPage"));
const PaymentHistoryPage = lazy(() => import("@/pages/profile/PaymentHistoryPage"));
const ProfileSettingsPage = lazy(() => import("@/pages/profile/ProfileSettingsPage"));

// Layout and Protected Route Components
import Layout from "@/components/layout/Layout";
import ProtectedRoute from "@/components/common/ProtectedRoute";

// Loading Fallback Component
const PageLoader = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
  </div>
);

export const routes: RouteObject[] = [
  {
    // Public Routes
    path: "/",
    element: (
      <Suspense fallback={<PageLoader />}>
        <LandingPage />
      </Suspense>
    ),
    index: true,
  },
  {
    path: "/privacy",
    element: (
      <Suspense fallback={<PageLoader />}>
        <PrivacyPolicyPage />
      </Suspense>
    ),
  },
  {
    path: "/contact",
    element: (
      <Suspense fallback={<PageLoader />}>
        <ContactPage />
      </Suspense>
    ),
  },
  {
    path: "/auth/login",
    element: (
      <Suspense fallback={<PageLoader />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: "/auth/register",
    element: (
      <Suspense fallback={<PageLoader />}>
        <RegisterPage />
      </Suspense>
    ),
  },
  {
    path: "/auth/callback",
    element: (
      <Suspense fallback={<PageLoader />}>
        <OAuthCallbackPage />
      </Suspense>
    ),
  },
  
  // Protected Routes with Layout
  {
    element: (
      <Suspense fallback={<PageLoader />}>
        <Layout />
      </Suspense>
    ),
    children: [
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: "/careers",
            element: (
              <Suspense fallback={<PageLoader />}>
                <CareerPathsPage />
              </Suspense>
            ),
          },
          // Links routes
          {
            path: "/links",
            element: (
              <Suspense fallback={<PageLoader />}>
                <RecentLinksPage />
              </Suspense>
            ),
          },
          {
            path: "/links/modern",
            element: (
              <Suspense fallback={<PageLoader />}>
                <ModernLinksPage />
              </Suspense>
            ),
          },
          {
            path: "/links/collections",
            element: (
              <Suspense fallback={<PageLoader />}>
                <CollectionsPage />
              </Suspense>
            ),
          },
          {
            path: "/links/collections/:collectionId",
            element: (
              <Suspense fallback={<PageLoader />}>
                <CollectionLinksPage />
              </Suspense>
            ),
          },
          {
            path: "/links/tags",
            element: (
              <Suspense fallback={<PageLoader />}>
                <TagsPage />
              </Suspense>
            ),
          },
          {
            path: "/links/tags/:tagId",
            element: (
              <Suspense fallback={<PageLoader />}>
                <TagLinksPage />
              </Suspense>
            ),
          },
          {
            path: "/links/filters",
            element: (
              <Suspense fallback={<PageLoader />}>
                <FiltersPage />
              </Suspense>
            ),
          },
          {
            path: "/links/import",
            element: (
              <Suspense fallback={<PageLoader />}>
                <ImportPage />
              </Suspense>
            ),
          },
          // Discover routes - updated route paths
          {
            path: "/discover/mine",
            element: (
              <Suspense fallback={<PageLoader />}>
                <MyActivityPage />
              </Suspense>
            ),
          },
          {
            path: "/discover/network-activity",
            element: (
              <Suspense fallback={<PageLoader />}>
                <NetworkActivityPage />
              </Suspense>
            ),
          },
          // Add redirects for old paths
          {
            path: "/discover/my-activity",
            element: (
              <Suspense fallback={<PageLoader />}>
                <MyActivityPage />
              </Suspense>
            ),
          },
          {
            path: "/discover/network-activity",
            element: (
              <Suspense fallback={<PageLoader />}>
                <NetworkActivityPage />
              </Suspense>
            ),
          },
          // Profile routes
          {
            path: "/profile/account-info",
            element: (
              <Suspense fallback={<PageLoader />}>
                <AccountInfoPage />
              </Suspense>
            ),
          },
          {
            path: "/profile/subscription",
            element: (
              <Suspense fallback={<PageLoader />}>
                <SubscriptionPlanPage />
              </Suspense>
            ),
          },
          {
            path: "/profile/payment-history",
            element: (
              <Suspense fallback={<PageLoader />}>
                <PaymentHistoryPage />
              </Suspense>
            ),
          },
          {
            path: "/profile/settings",
            element: (
              <Suspense fallback={<PageLoader />}>
                <ProfileSettingsPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  
  // Catch-all route
  {
    path: "*",
    element: (
      <Suspense fallback={<PageLoader />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
];

export default routes;